import { Color, Spacing } from '@persona-id/design-tokens';
import { ReactNode } from 'react';
import styled from 'styled-components';

import AttributeItem from 'dashboard/components/AttributeItem';
import { UppercaseLabel } from 'dashboard/components/Typography';
import {
  DatumTypeToken,
  NodeId,
  NodeRenderConfig,
  PersonaPropertyValues,
} from 'dashboard/models/graph';
import { HydratedNode } from 'dashboard/models/graph-hydration';
import { BASE } from 'design-system/variables/spacing';

const SectionTitle = styled(UppercaseLabel)`
  color: ${Color.CoreSlate900};
`;

const AttributesColumn = styled('div')`
  display: grid;
  grid-row-gap: ${Spacing[4]};
`;

interface GraphHoverCardPropertyProps {
  nodeId: NodeId;
  hydratedNode: HydratedNode;
  cardMenu?: boolean;
  getNodeRenderConfig: (token: DatumTypeToken) => NodeRenderConfig;
}

const GraphHoverCardProperty = ({
  nodeId,
  hydratedNode,
  cardMenu = false,
  getNodeRenderConfig,
}: GraphHoverCardPropertyProps) => {
  const propertyValuePairs = getPropertyValues(
    hydratedNode,
    getNodeRenderConfig(nodeId.token),
  ).filter(Boolean);

  return (
    <>
      {propertyValuePairs.length > 1 && (
        // We only show the title if there's more than one (property, value) pair to render on the screen.
        <SectionTitle>{getNodeRenderConfig(nodeId.token).displayName}</SectionTitle>
      )}
      <AttributesColumn>
        {propertyValuePairs.map((propertyValuePair, index) => (
          <AttributeItem
            title={propertyValuePair.property}
            value={propertyValuePair.value}
            isRedacted={false}
            wordBreak='break-all'
            placeholder='-'
            key={index}
            dataTest='graph-property-hover-card'
            titleSpacing={cardMenu ? 0 : BASE}
          />
        ))}
      </AttributesColumn>
    </>
  );
};

interface PropertyValuePair {
  property: string;
  value: ReactNode | ReactNode[];
}

// TODO(kerry): Make the rendering better once we get more hydration piped down.
const getPropertyValues = (
  hydratedNode: HydratedNode,
  nodeRenderConfig: NodeRenderConfig,
): PropertyValuePair[] => {
  switch (hydratedNode.nodeType) {
    case 'account_status':
      return [
        {
          property: nodeRenderConfig.displayName,
          value: hydratedNode.hydrationData.status,
        },
      ];
    case 'account_type':
      return [
        {
          property: nodeRenderConfig.displayName,
          value: hydratedNode.hydrationData.type,
        },
      ];
    case 'address': {
      const line1 = hydratedNode.hydrationData['address-line1'];
      const line2 = hydratedNode.hydrationData['address-line2'];
      const line3 = hydratedNode.hydrationData['address-line3'];

      const formattedAddress = (
        <>
          {line1}
          {line2 && <br />}
          {line2 && <>{line2}</>}
          {line3 && <br />}
          {line3 && <>{line3}</>}
        </>
      );
      return [
        {
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          property: null,
          value: formattedAddress,
        },
        {
          property: 'Country',
          value: hydratedNode.hydrationData['country-code'],
        },
      ];
    }
    case 'birthdate':
      return [
        {
          property: nodeRenderConfig.displayName,
          value: hydratedNode.hydrationData.birthdate,
        },
      ];
    case 'browser_fingerprint':
      return [
        {
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          property: nodeRenderConfig.contextMenu.length >= 1 ? null : nodeRenderConfig.displayName,
          value: hydratedNode.hydrationData['browser-fingerprint'],
        },
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        ...nodeRenderConfig.contextMenu.map((graphProperty) => {
          switch (graphProperty.value) {
            case PersonaPropertyValues.BrowserName: {
              return {
                property: 'Browser Name',
                value: hydratedNode.hydrationData['browser-names'],
              };
            }
          }
        }),
      ];
    case 'device_fingerprint':
      return [
        {
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          property: nodeRenderConfig.contextMenu.length >= 1 ? null : nodeRenderConfig.displayName,
          value: hydratedNode.hydrationData['device-fingerprint'],
        },
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        ...nodeRenderConfig.contextMenu.map((graphProperty) => {
          switch (graphProperty.value) {
            case PersonaPropertyValues.DeviceType: {
              return {
                property: 'Device Type',
                value: hydratedNode.hydrationData['device-types'],
              };
            }
          }
        }),
      ];
    case 'device_token':
      return [
        {
          property: nodeRenderConfig.displayName,
          value: hydratedNode.hydrationData.token,
        },
      ];
    case 'device_vendor_id':
      return [
        {
          property: nodeRenderConfig.displayName,
          value: hydratedNode.hydrationData['device-vendor-id'],
        },
      ];
    case 'email':
      return [
        {
          property: nodeRenderConfig.displayName,
          value: hydratedNode.hydrationData.email,
        },
      ];
    case 'government_id':
      return [
        {
          property: nodeRenderConfig.displayName,
          value: hydratedNode.hydrationData.id,
        },
      ];
    case 'id_document_number':
      return [
        {
          property: 'Document Number',
          value: hydratedNode.hydrationData['document-number'],
        },
        {
          property: 'Issuing Country',
          value: hydratedNode.hydrationData['issuing-country'],
        },
        {
          property: 'Issuing Subdivision',
          value: hydratedNode.hydrationData['issuing-subdivision'],
        },
      ];
    case 'id_number':
      return [
        {
          property: nodeRenderConfig.displayName,
          value: hydratedNode.hydrationData.id,
        },
      ];
    case 'name':
      return [
        {
          property: 'First Name',
          value: hydratedNode.hydrationData['name-first'],
        },
        {
          property: 'Middle Name',
          value: hydratedNode.hydrationData['name-middle'],
        },
        {
          property: 'Last Name',
          value: hydratedNode.hydrationData['name-last'],
        },
      ];
    case 'network':
      return [
        {
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          property: nodeRenderConfig.contextMenu.length >= 1 ? null : nodeRenderConfig.displayName,
          value: hydratedNode.hydrationData.ip,
        },
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        ...nodeRenderConfig.contextMenu.map((graphProperty) => {
          switch (graphProperty.value) {
            case PersonaPropertyValues.Location: {
              const formattedNetworkLocation =
                hydratedNode.hydrationData['region-name'] &&
                hydratedNode.hydrationData['country-name']
                  ? [
                      hydratedNode.hydrationData['region-name'],
                      hydratedNode.hydrationData['country-name'],
                    ]
                  : null;

              return {
                property: 'Location',
                value: formattedNetworkLocation,
              };
            }
            case PersonaPropertyValues.ProxyType: {
              return {
                property: 'Proxy Type',
                value: hydratedNode.hydrationData['proxy-type'],
              };
            }
            case PersonaPropertyValues.ThreatLevel: {
              return {
                property: 'Threat Level',
                value: hydratedNode.hydrationData['threat-level'],
              };
            }
          }
        }),
      ];
    case 'phone':
      return [
        {
          property: nodeRenderConfig.displayName,
          value: hydratedNode.hydrationData.phone,
        },
      ];
    case 'postal_code':
      return [
        {
          property: nodeRenderConfig.displayName,
          value: hydratedNode.hydrationData['postal-code'],
        },
      ];
    case 'ssn':
      return [
        {
          property: nodeRenderConfig.displayName,
          value: hydratedNode.hydrationData.ssn,
        },
      ];
    case 'ssn_last4':
      return [
        {
          property: nodeRenderConfig.displayName,
          value: hydratedNode.hydrationData['ssn-last4'],
        },
      ];
    case 'custom_datum_type':
      try {
        const hydrationValue = JSON.parse(hydratedNode.hydrationData.value);
        // JSON.parse does not raise if given a numbers (Ex: 12345). Do an extra check if the value is an object or not
        if (typeof hydrationValue !== 'object') {
          return [
            {
              property: nodeRenderConfig.displayName,
              value: hydratedNode.hydrationData.value,
            },
          ];
        }
        const orderArray = nodeRenderConfig.contextMenu.map((graphProperty) => graphProperty.value);
        return Object.entries(hydrationValue)
          .sort((a, b) => orderArray.indexOf(a[0]) - orderArray.indexOf(b[0]))
          .map(([key, value]) => ({
            property: key,
            value: value,
          }));
      } catch (e) {
        // If we can't parse the JSON (like when node_key_schema.size==1), just show the raw value.
        return [
          {
            property: nodeRenderConfig.displayName,
            value: hydratedNode.hydrationData.value,
          },
        ];
      }
    // Special rendering for the transactions demo.
    case 'external_account':
      return [
        {
          property: 'Routing Number',
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          value: hydratedNode.hydrationData['transaction-account-info']['routing-number'],
        },
        {
          property: 'Account Number',
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          value: hydratedNode.hydrationData['transaction-account-info']['account-number'],
        },
      ];
    case 'transactions':
      return [
        {
          property: 'Amount',
          value: hydratedNode.hydrationData.netVolume.toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
            maximumFractionDigits: 2,
          }),
        },
        {
          property: 'Number of transactions',
          value: hydratedNode.hydrationData.transactions.length,
        },
      ];
    default:
      return [
        {
          property: '',
          value: hydratedNode.nodeType,
        },
      ];
  }
};

export default GraphHoverCardProperty;
